/**
 * ScholarSync — Reset Password Page
 * File: frontend/src/ResetPassword.js
 *
 * User arrives via reset link with ?token=xxx.
 * Enters new password → backend verifies token → updates password.
 */

import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from './config';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Reset failed');
      }

      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F3F4F6',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: '40px 36px',
          maxWidth: 420,
          width: '100%',
          boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
          textAlign: 'center',
        }}>
          <h2 style={{ color: '#e53e3e' }}>Invalid Reset Link</h2>
          <p style={{ color: '#666' }}>This password reset link is missing or invalid.</p>
          <Link to="/forgot-password" style={{ color: '#4a6cf7', textDecoration: 'none' }}>
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#F3F4F6',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '20px',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: '40px 36px',
        maxWidth: 420,
        width: '100%',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
      }}>
        {success ? (
          <>
            <h2 style={{ margin: '0 0 12px', color: '#166534', textAlign: 'center' }}>
              Password updated!
            </h2>
            <p style={{ color: '#555', textAlign: 'center' }}>
              Redirecting you to login...
            </p>
          </>
        ) : (
          <>
            <h2 style={{ margin: '0 0 8px', color: '#1a1a2e', textAlign: 'center' }}>
              Set a new password
            </h2>
            <p style={{ color: '#666', textAlign: 'center', marginBottom: 24, fontSize: 14 }}>
              Enter your new password below.
            </p>

            <form onSubmit={handleSubmit}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: '#333', fontWeight: 500 }}>
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 15,
                  marginBottom: 16,
                  boxSizing: 'border-box',
                }}
              />

              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: '#333', fontWeight: 500 }}>
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 15,
                  marginBottom: 20,
                  boxSizing: 'border-box',
                }}
              />

              {error && (
                <p style={{ color: '#e53e3e', fontSize: 14, marginBottom: 16 }}>{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#4a6cf7',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Updating...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}